import { useEffect, useRef, useState } from "react"
import { Box, Card, CardActionArea, Chip, Fade, IconButton, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import tgePartnership from "assets/carousel/TGE partnership tile.png"
import goPeople from "assets/carousel/GoPeople partnership tile.png"
import fullfilment from "assets/carousel/customer fulfilment solutions tile.png"
import dfePartnership from "assets/carousel/Direct Freight Express partnership tile.png"

const C = {
  text: "#121828",
  muted: "#65748B",
  brand: "#F3DA00",
  surface: "#FFFFFF",
  border: "#E6E8F0",
}

const AUTO_ADVANCE_MS = 6000

// Static "what's new" content for the marketing carousel. Replace with a CMS
// feed or backend list when one is available.
const ITEMS = [
  {
    // Full-bleed hero: the image already carries the headline + CTA, so the
    // tag chip and title/blurb are suppressed for this slide (see `hero`).
    hero: true,
    tag: "Partnerships",
    title: "Expanded Team Global Express partnership — cheaper regional & rural freight",
    blurb: "We've extended our Team Global Express partnership to cut the cost of shipping to regional and rural Australia — up to 12% off with transparent dead-weight pricing and no surprise fees.",
    href: "https://www.estorelogistics.com.au/news-and-insights/team-global-express-partnership",
    image: tgePartnership,
  },
  {
    tag: "Partnerships",
    title: "Cost-effective next-day metro delivery: eStore Logistics partners with GoPeople",
    blurb: "We have enhanced our metro delivery capabilities by integrating a new specialised service designed to optimise shipping costs through dead-weight pricing.",
    href: "https://www.estorelogistics.com.au/news-and-insights/partnering-with-gopeople",
    image: goPeople,
  },
  {
    tag: "Fullfilment",
    title: "Fullfilment Solutions",
    blurb: "From storefronts to digital carts, our AI-powered fulfillment solutions are built to fit your business exactly.",
    href: "https://www.estorelogistics.com.au/services/fulfilment-solutions",
    image: fullfilment,
  },
  {
    tag: "Partnerships",
    title: "Oversized cartons, pallets, or B2B: eStore partners with Direct Freight Express to scale specialised freight capabilities",
    blurb:
      "We have expanded our national network by integrating Direct Freight Express (DFE), providing a high-performance solution for oversized shipments with the same automated efficiency as our parcel services.",
    href: "https://www.estorelogistics.com.au/news-and-insights/partnering-with-direct-freight-express",
    image: dfePartnership,
  },
]

const MarketingBanner = () => {
  const [idx, setIdx] = useState(0)
  const [paused, setPaused] = useState(false)
  const timerRef = useRef(null)

  // Auto-cycle every AUTO_ADVANCE_MS. Reset whenever idx, paused, or item count
  // changes so a manual click feels like a fresh cycle (not abruptly snapped).
  useEffect(() => {
    if (paused) return
    timerRef.current = setTimeout(() => {
      setIdx(i => (i + 1) % ITEMS.length)
    }, AUTO_ADVANCE_MS)
    return () => clearTimeout(timerRef.current)
  }, [idx, paused])

  const goTo = i => setIdx(((i % ITEMS.length) + ITEMS.length) % ITEMS.length)
  const prev = e => {
    e.stopPropagation()
    e.preventDefault()
    goTo(idx - 1)
  }
  const next = e => {
    e.stopPropagation()
    e.preventDefault()
    goTo(idx + 1)
  }

  const item = ITEMS[idx]

  return (
    <Card
      elevation={0}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        border: `1px solid ${C.border}`,
        overflow: "hidden",
        position: "relative",
        bgcolor: C.surface,
        transition: "border-color 180ms, transform 180ms, box-shadow 180ms",
        "&:hover": {
          borderColor: alpha(C.text, 0.18),
          transform: "translateY(-1px)",
          boxShadow: "0 6px 24px -12px rgba(18, 24, 40, 0.18)",
        },
      }}
    >
      <CardActionArea component="a" href={item.href} target="_blank" rel="noopener noreferrer" sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}>
        {/* Image hero — fades between slides; grows to fill the card height
            so the banner card stays the same size as its row neighbours. */}
        <Box sx={{ position: "relative", width: "100%", flex: 1, minHeight: 100, overflow: "hidden", bgcolor: "#F1F2F4" }}>
          {ITEMS.map((it, i) => (
            <Fade key={it.title} in={i === idx} timeout={{ enter: 500, exit: 300 }}>
              <Box
                component="img"
                src={it.image}
                alt={it.title}
                loading="lazy"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Fade>
          ))}

          {/* Tag chip overlaid (hidden for hero slides, whose image carries its own text) */}
          {!item.hero && (
            <Fade key={item.tag} in timeout={400}>
              <Chip
                label={item.tag}
                size="small"
                sx={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  bgcolor: alpha("#000000", 0.7),
                  color: "white",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: 0.4,
                  backdropFilter: "blur(6px)",
                  "& .MuiChip-label": { px: 1.25 },
                }}
              />
            </Fade>
          )}
        </Box>

        {/* Title + blurb — hidden for hero slides (image already carries them) */}
        {!item.hero && (
          <Box sx={{ px: 2, py: 1.25, flexShrink: 0 }}>
            <Fade key={`title-${idx}`} in timeout={400}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.text, mb: 0.25, lineHeight: 1.3 }}>{item.title}</Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: C.muted,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.4,
                    fontSize: 11,
                  }}
                >
                  {item.blurb}
                </Typography>
              </Box>
            </Fade>
          </Box>
        )}
      </CardActionArea>

      {/* Side arrows — only appear on hover */}
      <IconButton
        onClick={prev}
        size="small"
        aria-label="Previous"
        sx={{
          position: "absolute",
          left: 8,
          top: "calc(50% - 60px)",
          transform: "translateY(-50%)",
          bgcolor: alpha("#FFFFFF", 0.95),
          color: C.text,
          width: 28,
          height: 28,
          opacity: 0,
          transition: "opacity 180ms",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          ".MuiCard-root:hover &": { opacity: 1 },
          "&:hover": { bgcolor: "white" },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <IconButton
        onClick={next}
        size="small"
        aria-label="Next"
        sx={{
          position: "absolute",
          right: 8,
          top: "calc(50% - 60px)",
          transform: "translateY(-50%)",
          bgcolor: alpha("#FFFFFF", 0.95),
          color: C.text,
          width: 28,
          height: 28,
          opacity: 0,
          transition: "opacity 180ms",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          ".MuiCard-root:hover &": { opacity: 1 },
          "&:hover": { bgcolor: "white" },
        }}
      >
        <ChevronRightIcon sx={{ fontSize: 20 }} />
      </IconButton>

      {/* Pagination dots */}
      <Stack direction="row" spacing={0.75} justifyContent="center" sx={{ pb: 1.5, pt: 0.5, flexShrink: 0 }}>
        {ITEMS.map((_, i) => {
          const active = i === idx
          return (
            <Box
              key={i}
              onClick={e => {
                e.stopPropagation()
                goTo(i)
              }}
              sx={{
                height: 6,
                width: active ? 18 : 6,
                borderRadius: 3,
                bgcolor: active ? C.text : alpha(C.text, 0.18),
                cursor: "pointer",
                transition: "width 240ms, background-color 240ms",
              }}
            />
          )
        })}
      </Stack>
    </Card>
  )
}

export default MarketingBanner
